import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events"; // 22.08.2026 neu: fuer den variantUpdate-Listener (wie assets/gravur-text.js)

/**
 * Personalisierer — Gravurbaustein.
 *
 * Grundgedanke: Die Anzahl wird nicht gewählt, sondern gezählt. Der Kunde
 * klickt die Steine an, die graviert werden sollen; der Baustein schaltet
 * daraufhin die passende Produktvariante. Dadurch kann der Preis nie im
 * Widerspruch zur Gravur stehen.
 *
 * Der Variantenwechsel läuft über den Varianten-Picker des Themes (dessen
 * Radio-Feld wird angeklickt), damit Preis, Adresszeile und Bildergalerie vom
 * Theme selbst aktualisiert werden.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 22.08.2026 — zwei Korrekturen, sinngemäß übernommen aus `assets/gravur-text.js`
 * (dort am 17.08.2026 eingebaut und seither live). Die Gravurpflicht ließ sich
 * hier bis heute umgehen: der Artikel landete ohne Gravurtext im Warenkorb.
 *
 * 1. Die Knopfsuche läuft jetzt über den ABSCHNITT statt über das Formular.
 *    Der Knopf der Sticky-Kaufleiste steht außerhalb von `</form>`, aber
 *    innerhalb der Section — über das Formular gesucht wurde er nie gefunden
 *    und nie gesperrt. (`#sperren`, `#entsperren`)
 * 2. Es wird auf `variantUpdate` gehört. Das Theme entsperrt den Kaufknopf bei
 *    jedem Variantenwechsel selbst (`product-form.js` → `enable()`); dieser
 *    Baustein schaltet die Variante über `#varianteSchalten()` aber SELBST um,
 *    und der Varianten-Picker liefert das Ereignis bei einem Cache-Fehler
 *    asynchron — also erst NACH `#pruefen()`. Ohne den Listener ist die Sperre
 *    danach weg. (`#varianteGewechselt`)
 *
 * Bewusst NICHT angefasst: der interne `#gesperrt`-Merker (in gravur-text.js
 * wurde er gestrichen), der `#form`-Getter und alles Übrige. Das ist die
 * Entscheidung des Autors, nicht unsere.
 * ─────────────────────────────────────────────────────────────────────────
 */
class PersonalisiererComponent extends Component {
  #gesperrt = false;
  #aktiv = null;

  connectedCallback() {
    super.connectedCallback();

    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.formId;
    this.optionName = this.dataset.optionName || "";
    this.maxGravuren = parseInt(this.dataset.maxGravuren, 10) || 1;
    this.zeichenLimit = parseInt(this.dataset.zeichenLimit, 10) || 27;
    this.pflicht = this.dataset.pflicht === "true";

    // Zustand je Stein: { name, hex, gewaehlt, text }
    this.steine = new Map();
    this.#alle(".perso__stein").forEach((knopf) => {
      this.steine.set(parseInt(knopf.dataset.index, 10), {
        name: knopf.dataset.stein,
        hex: knopf.dataset.hex,
        gewaehlt: false,
        text: "",
      });
    });

    // 22.08.2026 neu: Auf Variantenwechsel hören — das Theme entsperrt den
    // Kaufknopf dabei selbst, die Gravursperre muss danach erneut gesetzt
    // werden. Begründung siehe #varianteGewechselt().
    this.onVariantUpdate = () => this.#varianteGewechselt();
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.#themePickerAusblenden();
    this.#allesAktualisieren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    // 22.08.2026 neu: Gegenstück zur Anmeldung oben — sonst bleibt der
    // Listener nach dem Entfernen des Bausteins am document hängen.
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    this.#entsperren();
  }

  /* ------------------------------------------------------------- Helfer */

  #alle(selektor) {
    return Array.from(this.querySelectorAll(selektor));
  }

  #imFeld(index, selektor) {
    return this.querySelector(`${selektor}[data-index="${index}"]`);
  }

  // 22.08.2026: seit der Umstellung von #sperren/#entsperren auf #section
  // unbenutzt. Bewusst stehen gelassen — Streichen ist die Entscheidung des
  // Autors, nicht Teil dieser Korrektur.
  get #form() {
    return document.getElementById(this.formId);
  }

  get #section() {
    return document.getElementById(`shopify-section-${this.sectionId}`) || document;
  }

  /** Angetippt — das Eingabefeld ist offen, muss aber noch gefüllt werden. */
  get #offene() {
    return [...this.steine.entries()]
      .filter(([, s]) => s.gewaehlt)
      .map(([i]) => i);
  }

  /**
   * Tatsächlich graviert: angetippt UND Text eingegeben.
   * Nur diese zählen für Haken, Preis und Variante — sonst zahlt jemand den
   * Aufpreis für einen Stein, auf dem am Ende nichts steht.
   */
  get #gravierte() {
    return [...this.steine.entries()]
      .filter(([, s]) => s.gewaehlt && s.text.trim() !== "")
      .map(([i]) => i);
  }

  /**
   * Blendet die Anzahl-Auswahl des Themes aus — sie wird von diesem Baustein
   * gesteuert. Sie bleibt im DOM, damit ihre Radio-Felder klickbar bleiben.
   */
  #themePickerAusblenden() {
    if (!this.optionName) return;

    this.#radios().forEach((radio) => {
      const gruppe = radio.closest(".variant-option, .variant-picker__option, fieldset");
      if (gruppe) gruppe.classList.add("perso-ausgeblendet");
    });
  }

  /** Alle Radio-Felder der Anzahl-Option im Varianten-Picker des Themes. */
  #radios() {
    if (!this.optionName) return [];
    return Array.from(this.#section.querySelectorAll('input[type="radio"]')).filter((r) =>
      r.name?.startsWith(this.optionName)
    );
  }

  /* -------------------------------------------------------- Ereignisse */

  /**
   * Schließt angetippte Steine, in die nichts eingetragen wurde.
   * Es soll immer höchstens einer offen sein — sonst bleiben gestrichelte
   * Steine stehen, die weder graviert werden noch bearbeitet werden.
   */
  #leereSchliessen(ausser = null) {
    for (const [i, s] of this.steine) {
      if (i !== ausser && s.gewaehlt && s.text.trim() === "") {
        s.gewaehlt = false;
        if (this.#aktiv === i) this.#aktiv = null;
      }
    }
  }

  steinUmschalten(event) {
    // event.target — nicht currentTarget. Das Theme hört global auf document
    // und reicht die Methode mit einem Proxy-Event nach; currentTarget ist
    // dann nicht mehr gesetzt. Der Proxy liefert in target das Element mit
    // dem on:click-Attribut (assets/component.js:224).
    const knopf = event.target;
    const index = parseInt(knopf.dataset.index, 10);
    const stein = this.steine.get(index);
    if (!stein) return;

    // Ein anderer Stein wird angefasst → leere offene Steine räumen.
    this.#leereSchliessen(index);

    if (stein.gewaehlt) {
      // Abwählen erst, wenn die Gravur gelöscht ist — sonst ginge Text
      // unbemerkt verloren.
      if (stein.text.trim() !== "") {
        this.#meldung(this.dataset.meldungAbwaehlen.replace("[stein]", stein.name));
        this.#eingabeZeigen(index);
        return;
      }
      stein.gewaehlt = false;
      if (this.#aktiv === index) this.#aktiv = null;
    } else {
      if (this.#offene.length >= this.maxGravuren) {
        this.#meldung(this.dataset.meldungMax.replace("[max]", this.maxGravuren));
        return;
      }
      stein.gewaehlt = true;
      this.#aktiv = index;
    }

    this.#allesAktualisieren();
    if (stein.gewaehlt) this.#eingabeZeigen(index);
  }

  textGeaendert(event) {
    const eingabe = event.target;
    const index = parseInt(eingabe.dataset.index, 10);
    const stein = this.steine.get(index);
    if (!stein) return;

    stein.text = eingabe.value;

    const zaehler = this.#imFeld(index, ".perso__zaehler");
    if (zaehler) {
      zaehler.textContent = `${eingabe.value.length}/${this.zeichenLimit}`;
      zaehler.classList.toggle("is-voll", eingabe.value.length >= this.zeichenLimit);
    }

    this.#allesAktualisieren();
  }

  /* ------------------------------------------------------ Darstellung */

  #eingabeZeigen(index) {
    this.#aktiv = index;

    this.#alle(".perso__eingabe").forEach((feld) => {
      const i = parseInt(feld.dataset.index, 10);
      const zeigen = i === index && this.steine.get(i)?.gewaehlt;
      feld.hidden = !zeigen;
    });

    const eingabe = this.#imFeld(index, ".perso__input");
    if (!eingabe) return;

    // Auf dem Handy liegt das Feld nach dem Antippen oft unterhalb des
    // sichtbaren Bereichs. Erst hinscrollen, dann fokussieren — umgekehrt
    // springt die Seite, sobald die Tastatur aufgeht.
    const feld = eingabe.closest(".perso__eingabe") || eingabe;
    feld.scrollIntoView({ behavior: "smooth", block: "center" });

    requestAnimationFrame(() => eingabe.focus({ preventScroll: true }));
  }

  #allesAktualisieren() {
    const offene = this.#offene;
    const gravierte = this.#gravierte;

    this.#alle(".perso__stein").forEach((knopf) => {
      const i = parseInt(knopf.dataset.index, 10);
      const stein = this.steine.get(i);
      const graviert = stein.gewaehlt && stein.text.trim() !== "";

      // Haken nur bei echter Gravur. Ein bloss angetippter Stein bekommt den
      // Bearbeitungszustand — sonst wirkt er ausgewaehlt, obwohl er weder
      // graviert wird noch bezahlt werden soll.
      knopf.classList.toggle("is-graviert", graviert);
      knopf.classList.toggle("is-offen", stein.gewaehlt && !graviert);
      knopf.setAttribute("aria-pressed", graviert ? "true" : "false");

      const anzeige = this.#imFeld(i, ".perso__stein-text");
      if (anzeige) anzeige.textContent = stein.text.trim();
    });

    this.#alle(".perso__eingabe").forEach((feld) => {
      const i = parseInt(feld.dataset.index, 10);
      if (!this.steine.get(i)?.gewaehlt) feld.hidden = true;
    });

    if (this.#aktiv === null && offene.length) {
      const nochLeer = offene.find((i) => this.steine.get(i).text.trim() === "");
      this.#eingabeZeigen(nochLeer ?? offene[0]);
    }

    this.#ausgabenSchreiben();
    this.#zusammenfassungSchreiben(gravierte.length);
    this.#varianteSchalten(gravierte.length);
    this.#pruefen(offene);
  }

  /** Schreibt die Line-Item-Properties ins Produktformular. */
  #ausgabenSchreiben() {
    this.#alle('input[type="hidden"][data-index]').forEach((feld) => {
      const i = parseInt(feld.dataset.index, 10);
      const stein = this.steine.get(i);
      const zaehlt = stein?.gewaehlt && stein.text.trim() !== "";

      // Deaktivierte Felder sendet der Browser nicht mit.
      feld.disabled = !zaehlt;
      feld.value = zaehlt ? stein.text.trim() : "";
    });
  }

  #zusammenfassungSchreiben(anzahl) {
    const zeile = this.refs.zusammenfassung;
    if (!zeile) return;

    // Bleibt immer stehen — der Preishinweis daneben soll auch vor der ersten
    // Auswahl lesbar sein.
    zeile.textContent = (this.dataset.zusammenfassung || "")
      .replace("[anzahl]", anzahl)
      .replace("[max]", this.maxGravuren);
  }

  /**
   * Schaltet die Produktvariante über den Varianten-Picker des Themes.
   * Dadurch aktualisiert das Theme Preis, URL und Galerie selbst.
   */
  #varianteSchalten(anzahl) {
    if (anzahl < 1) return;

    const radios = this.#radios();
    if (!radios.length) return;

    const ziel = radios.find((r) => parseInt(r.value, 10) === anzahl);
    if (!ziel || ziel.checked) return;

    ziel.click();
  }

  /* ----------------------------------------------- Sperren & Meldungen */

  /**
   * 22.08.2026 neu. Das Theme ruft bei jedem Variantenwechsel `enable()` auf
   * (`product-form.js`) und hebt damit unsere Sperre auf. Dieser Baustein löst
   * den Wechsel über `#varianteSchalten()` selbst aus, und der Varianten-Picker
   * liefert das Ereignis bei einem Cache-Fehler asynchron nach — der erste
   * gravierte Stein ist immer ein Cache-Fehler, das ist also der Regelfall.
   * Deshalb wird NACH dem Theme erneut geprüft (requestAnimationFrame).
   *
   * Abweichung zu `gravur-text.js`: dort läuft `#allesAktualisieren()`. Hier
   * bewusst nur `#pruefen()` — `#allesAktualisieren()` würde über
   * `#varianteSchalten()` aus einem Varianten-Ereignis heraus einen weiteren
   * Klick auf den Picker auslösen. Gesperrt/entsperrt wird ausschließlich in
   * `#pruefen()`, mehr braucht es hier nicht.
   */
  #varianteGewechselt() {
    requestAnimationFrame(() => this.#pruefen(this.#offene));
  }

  #pruefen(offene) {
    if (!this.pflicht) return;

    const ohneText = offene.filter((i) => this.steine.get(i).text.trim() === "");

    if (offene.length === 0) {
      this.#meldung(this.dataset.meldungLeer);
      this.#sperren();
    } else if (ohneText.length) {
      const name = this.steine.get(ohneText[0]).name;
      this.#meldung(this.dataset.meldungFehlt.replace("[stein]", name), ohneText[0]);
      this.#sperren();
    } else {
      this.#meldung(null);
      this.#entsperren();
    }
  }

  /** Zeigt eine Meldung; mit sprungZu wird sie anklickbar. */
  #meldung(text, sprungZu = null) {
    const kasten = this.refs.hinweis;
    const inhalt = this.refs.hinweisText;
    if (!kasten || !inhalt) return;

    kasten.hidden = !text;
    if (!text) {
      kasten.onclick = null;
      kasten.classList.remove("is-klickbar");
      return;
    }

    inhalt.textContent = text;
    kasten.classList.toggle("is-klickbar", sprungZu !== null);
    kasten.onclick = sprungZu !== null ? () => this.#eingabeZeigen(sprungZu) : null;
  }

  #sperren() {
    // 22.08.2026 geändert: Suche über den ABSCHNITT statt über das Formular
    // (vorher `this.#form`). Der Knopf der Sticky-Kaufleiste liegt außerhalb
    // von `</form>`, aber innerhalb der Section — über das Formular gesucht
    // wurde er nie gesperrt, die Gravurpflicht war damit umgehbar.
    // Gleiche Korrektur wie in `assets/gravur-text.js` (17.08.2026).
    this.#section.querySelectorAll('button[type="submit"], .add-to-cart-button').forEach((b) => {
      b.setAttribute("disabled", "");
      b.dataset.persoGesperrt = "true";
    });

    this.#section
      .querySelectorAll(".shopify-payment-button, .accelerated-checkout-block")
      .forEach((el) => el.classList.add("perso-ausgeblendet"));

    this.#gesperrt = true;
  }

  #entsperren() {
    if (!this.#gesperrt) return;

    // 22.08.2026 geändert: wie in #sperren() über den Abschnitt statt über das
    // Formular (vorher `this.#form`) — sonst bliebe der Sticky-Knopf nach dem
    // Entsperren dauerhaft deaktiviert. Es werden weiterhin nur Knöpfe mit
    // unserer eigenen Markierung angefasst; ein vom Theme wegen "ausverkauft"
    // gesetztes `disabled` bleibt unangetastet.
    this.#section.querySelectorAll('[data-perso-gesperrt="true"]').forEach((b) => {
      b.removeAttribute("disabled");
      delete b.dataset.persoGesperrt;
    });

    this.#section
      .querySelectorAll(".shopify-payment-button, .accelerated-checkout-block")
      .forEach((el) => el.classList.remove("perso-ausgeblendet"));

    this.#gesperrt = false;
  }
}

if (!customElements.get("personalisierer-component")) {
  customElements.define("personalisierer-component", PersonalisiererComponent);
}
